import { css, token } from '@terra/ui-styled-system';

export const baseStyles = css({
  // Root container
  '&.wmde-markdown, &&': {
    fontSize: 'var(--markdown-font-size)',
    lineHeight: 1.6,
    color: 'inherit',
  },

  // Hide anchor links
  '&.wmde-markdown .anchor, && .anchor': {
    display: 'none',
  },

  // Paragraphs
  '&.wmde-markdown p, && p': {
    fontSize: 'var(--markdown-font-size)',
    lineHeight: 1.6,
    margin: '0.5em 0',
    '&:first-child': { marginTop: 0 },
    '&:last-child': { marginBottom: 0 },
  },

  // Line breaks
  '&.wmde-markdown br, && br': {
    lineHeight: 1.6,
  },

  // Headings - consistent with PDF design system
  '&.wmde-markdown h1, && h1': {
    fontSize: 'var(--markdown-font-size) !important',
    fontWeight: 'var(--markdown-heading-font-weight)',
    color: token('colors.fg.default'),
    margin: '0.5em 0 0.2em !important',
    borderBottom: 'none !important',
    paddingBottom: '0 !important',
    '&:first-child': { marginTop: '0 !important' },
  },
  '&.wmde-markdown h2, && h2': {
    fontSize: 'var(--markdown-font-size) !important',
    fontWeight: 'var(--markdown-heading-font-weight)',
    color: token('colors.fg.default'),
    margin: '0.4em 0 0.15em !important',
    borderBottom: 'none !important',
    paddingBottom: '0 !important',
    '&:first-child': { marginTop: '0 !important' },
  },
  '&.wmde-markdown h3, && h3, &.wmde-markdown h4, && h4, &.wmde-markdown h5, && h5, &.wmde-markdown h6, && h6':
    {
      fontSize: 'var(--markdown-font-size) !important',
      fontWeight: 'var(--markdown-subheading-font-weight)',
      color: token('colors.fg.default'),
      margin: '0.3em 0 0.1em !important',
      '&:first-child': { marginTop: '0 !important' },
    },

  // Lists
  '&.wmde-markdown ul, && ul': {
    listStyleType: 'disc',
    fontSize: 'var(--markdown-font-size)',
    paddingLeft: '1.5em',
    margin: '0.5em 0',
  },
  '&.wmde-markdown ol, && ol': {
    listStyleType: 'decimal',
    fontSize: 'var(--markdown-font-size)',
    paddingLeft: '1.5em',
    margin: '0.5em 0',
  },
  '&.wmde-markdown li, && li': {
    fontSize: 'var(--markdown-font-size)',
    lineHeight: 1.6,
    margin: '0.25em 0',
  },

  // Links - subtle underline on hover
  '&.wmde-markdown a, && a': {
    color: 'var(--colors-colorPalette-default)',
    textDecoration: 'none',
    borderBottom: '1px solid transparent',
    transition: 'border-color 0.15s ease',
    '&:hover': {
      borderBottomColor: 'currentColor',
    },
  },

  // Blockquotes - elegant left border
  '&.wmde-markdown blockquote, && blockquote': {
    margin: `${token('spacing.3')} 0`,
    paddingLeft: token('spacing.4'),
    borderLeft: `3px solid ${token('colors.border.default')}`,
    color: token('colors.fg.muted'),
    fontStyle: 'italic',
    fontWeight: token('fontWeights.normal'),
    '& p': {
      margin: `${token('spacing.1')} 0`,
    },
  },

  // Horizontal rules
  '&.wmde-markdown hr, && hr': {
    width: '100%',
    height: '1px !important',
    border: 'none !important',
    borderTop: 'none !important',
    borderBottom: 'none !important',
    background: `${token('colors.border.subtle')} !important`,
    opacity: 0.4,
    margin: '0.6em 0 !important',
    padding: '0 !important',
  },

  // Code blocks - Terminal-style black background
  '&.wmde-markdown pre, && pre': {
    fontSize: 'var(--markdown-font-size-code)',
    fontFamily: token('fonts.mono'),
    fontWeight: 400,
    lineHeight: 1.5,
    whiteSpace: 'pre-wrap',
    wordWrap: 'break-word',
    overflowWrap: 'break-word',
    wordBreak: 'break-all',
    maxWidth: '100%',
    overflowX: 'auto',
    paddingTop: token('spacing.3'),
    paddingRight: token('spacing.4'),
    paddingBottom: token('spacing.3'),
    paddingLeft: token('spacing.2'),
    margin: `${token('spacing.3')} 0`,
    borderRadius: token('radii.md'),
    background: token('colors.gray.12'),
    border: `1px solid ${token('colors.gray.11')}`,
    borderTop: `3px solid ${token('colors.terra.9')}`,
    boxShadow: token('shadows.sm'),
    color: token('colors.gray.2'),
    position: 'relative',
  },
  // Code inside pre blocks
  '&.wmde-markdown pre code, && pre code': {
    fontSize: 'var(--markdown-font-size-code)',
    fontFamily: 'inherit',
    fontWeight: 'inherit',
    lineHeight: 'inherit',
    color: 'inherit',
    background: 'transparent',
    padding: '0 !important',
    borderRadius: 0,
    whiteSpace: 'inherit',
    wordBreak: 'inherit',
  },
  '&.wmde-markdown pre code.md-code-show-line-numbers, && pre code.md-code-show-line-numbers':
    {
      counterReset: 'md-code-line',
    },
  '&.wmde-markdown pre code .code-line, && pre code .code-line': {
    display: 'block',
  },
  '&.wmde-markdown pre code.md-code-show-line-numbers .code-line, && pre code.md-code-show-line-numbers .code-line':
    {
      position: 'relative',
      paddingLeft: 'calc(3ch + 1em)',
      counterIncrement: 'md-code-line',
    },
  '&.wmde-markdown pre code.md-code-show-line-numbers .code-line.md-code-line-omit, && pre code.md-code-show-line-numbers .code-line.md-code-line-omit':
    {
      counterIncrement: 'none',
      color: 'var(--color-prettylights-syntax-comment)',
    },
  '&.wmde-markdown pre code.md-code-show-line-numbers .code-line.md-code-line-omit *, && pre code.md-code-show-line-numbers .code-line.md-code-line-omit *':
    {
      color: 'inherit',
    },
  '&.wmde-markdown pre code.md-code-show-line-numbers .code-line::before, && pre code.md-code-show-line-numbers .code-line::before':
    {
      content: 'counter(md-code-line)',
      position: 'absolute',
      left: 0,
      width: '3ch',
      textAlign: 'right',
      color: 'var(--color-prettylights-syntax-comment)',
      userSelect: 'none',
      pointerEvents: 'none',
    },
  '&.wmde-markdown pre code.md-code-show-line-numbers .code-line.md-code-line-omit::before, && pre code.md-code-show-line-numbers .code-line.md-code-line-omit::before':
    {
      content: 'none',
    },
  // Standalone code (not in pre)
  '&.wmde-markdown code:not(pre code), && code:not(pre code)': {
    fontSize: 'var(--markdown-font-size-code)',
    fontFamily: token('fonts.mono'),
    fontWeight: 400,
    lineHeight: 1.4,
  },

  // Inline code - subtle background
  '&.wmde-markdown p code:not(pre code), && p code:not(pre code), &.wmde-markdown li code:not(pre code), && li code:not(pre code)':
    {
      fontSize: 'var(--markdown-font-size-code)',
      fontWeight: 400,
      padding: `${token('spacing.0.5')} ${token('spacing.1.5')}`,
      borderRadius: token('radii.sm'),
      background: token('colors.bg.subtle'),
      color: token('colors.fg.severity.critical'),
      border: `1px solid ${token('colors.border.subtle')}`,
      fontFamily: token('fonts.mono'),
      whiteSpace: 'normal',
      wordBreak: 'break-word',
    },

  '&.wmde-markdown .markdown-table-wrap, && .markdown-table-wrap': {
    width: '100%',
    maxWidth: '100%',
    margin: '0.75em 0',
    overflowX: 'auto',
    overflowY: 'hidden',
  },
  '&.wmde-markdown table, && table': {
    fontSize: 'var(--markdown-font-size-small)',
    display: 'table',
    width: 'max-content',
    minWidth: '100%',
    maxWidth: 'none',
    borderCollapse: 'collapse',
    tableLayout: 'auto',
    margin: 0,
    boxShadow: 'none !important',
  },
  '&.wmde-markdown th, && th': {
    fontSize: 'var(--markdown-font-size-small)',
    fontWeight: '500 !important',
    padding: '0.55em 0.8em',
    textAlign: 'left',
    whiteSpace: 'nowrap',
    color: `${token('colors.fg.default')} !important`,
    border: `1px solid ${token('colors.border.default')}`,
    background: token('colors.bg.muted'),
  },
  '&.wmde-markdown td, && td': {
    fontSize: 'var(--markdown-font-size-small)',
    padding: '0.55em 0.8em',
    whiteSpace: 'normal',
    wordBreak: 'normal',
    overflowWrap: 'break-word',
    verticalAlign: 'top',
    color: `${token('colors.fg.default')} !important`,
    border: `1px solid ${token('colors.border.default')}`,
  },
  '&.wmde-markdown table code:not(pre code), && table code:not(pre code)': {
    fontSize: 'var(--markdown-font-size-code)',
    fontFamily: token('fonts.mono'),
    fontWeight: 400,
    lineHeight: 1.35,
    padding: `${token('spacing.0.5')} ${token('spacing.1.5')}`,
    borderRadius: token('radii.sm'),
    background: `${token('colors.bg.muted')} !important`,
    color: `${token('colors.fg.default')} !important`,
    border: `1px solid ${token('colors.border.neutral.muted')}`,
    whiteSpace: 'nowrap',
  },

  // Strong/Bold
  '&.wmde-markdown strong, && strong': {
    fontWeight: 'var(--markdown-strong-font-weight)',
  },

  // Emphasis/Italic
  '&.wmde-markdown em, && em': {
    fontStyle: 'italic',
  },

  // Escaped HTML tags - display as monospace text
  '&.wmde-markdown .escaped-html, && .escaped-html': {
    fontSize: 'var(--markdown-font-size-code)',
    fontFamily: token('fonts.mono'),
    lineHeight: 1.4,
    whiteSpace: 'pre-wrap',
    wordWrap: 'break-word',
    overflowWrap: 'break-word',
    wordBreak: 'break-all',
    maxWidth: '100%',
    overflowX: 'auto',
    padding: '0.5em 0.75em',
    margin: '0.35em 0',
    borderRadius: '6px',
    background: 'var(--colors-bg-subtle)',
    border: '1px solid var(--colors-border-subtle)',
  },

  // Escaped link container - wrapper for link text and URL
  '&.wmde-markdown .escaped-link-container, && .escaped-link-container': {
    display: 'inline',
  },

  // Escaped links - styled like links but not clickable (span elements)
  '&.wmde-markdown .escaped-link, && .escaped-link': {
    color: 'var(--color-accent-fg)',
    textDecoration: 'none',
    borderBottom: '1px solid transparent',
    transition: 'border-color 0.15s ease',
    pointerEvents: 'auto',
    display: 'inline',
    fontWeight: 'inherit',
    fontStyle: 'inherit',
    position: 'relative',
  },

  // URL text (converted from links)
  '&.wmde-markdown .url-text, && .url-text': {
    fontSize: 'var(--markdown-font-size-code)',
    color: 'var(--colors-colorPalette-default)',
    backgroundColor: 'var(--colors-bg-subtle)',
    padding: '0.1em 0.3em',
    borderRadius: '3px',
    wordBreak: 'break-all',
    display: 'inline-block',
  },

  // Images
  '&.wmde-markdown img, && img': {
    maxWidth: '100%',
    borderRadius: '6px',
  },

  // Copy button in code blocks
  '&.wmde-markdown .copied, && .copied': {
    opacity: 0.5,
    transition: 'opacity 0.15s ease',
    '&:hover': {
      opacity: 1,
    },
    '&.active': {
      opacity: 1,
    },
  },
});

export const compactOverrides = css({
  '&.wmde-markdown, &&': {
    padding: 0,
    margin: 0,
    lineHeight: 1.6,
  },
  '&.wmde-markdown p, && p': {
    lineHeight: 1.6,
    margin: '0.6em 0 !important',
  },
  '&.wmde-markdown pre, && pre': {
    paddingTop: '0.4em',
    paddingRight: '0.6em',
    paddingBottom: '0.4em',
    paddingLeft: '0.35em',
    margin: '0.25em 0 !important',
    border: 'none',
    background: 'transparent',
  },
  '&.wmde-markdown pre code.md-code-show-line-numbers .code-line, && pre code.md-code-show-line-numbers .code-line':
    {
      paddingLeft: 'calc(3ch + 0.75em)',
    },
  '&.wmde-markdown blockquote, && blockquote': {
    margin: '0.25em 0 !important',
    paddingLeft: '0.75em',
  },
  '&.wmde-markdown ul, && ul, &.wmde-markdown ol, && ol': {
    margin: '0.42em 0 !important',
    paddingLeft: '1.2em',
  },
  '&.wmde-markdown li, && li': {
    lineHeight: 1.6,
    margin: '0.25em 0 !important',
  },
  '&.wmde-markdown .markdown-table-wrap, && .markdown-table-wrap': {
    margin: '0.35em 0 !important',
  },
  '&.wmde-markdown table, && table': {
    margin: '0 !important',
  },
  '&.wmde-markdown h1, && h1, &.wmde-markdown h2, && h2, &.wmde-markdown h3, && h3':
    {
      lineHeight: 1.4,
      margin: '0.85em 0 0.3em !important',
    },
  '&.wmde-markdown h4, && h4, &.wmde-markdown h5, && h5, &.wmde-markdown h6, && h6':
    {
      lineHeight: 1.4,
      margin: '0.85em 0 0.3em !important',
    },
  '&.wmde-markdown > *:first-child, && > *:first-child': {
    marginTop: '0 !important',
  },
  '&.wmde-markdown > *:last-child, && > *:last-child': {
    marginBottom: '0 !important',
  },
  '&.wmde-markdown p + ul, && p + ul, &.wmde-markdown p + ol, && p + ol': {
    marginTop: '0.26em !important',
  },
  '&.wmde-markdown h1 + p, && h1 + p, &.wmde-markdown h2 + p, && h2 + p, &.wmde-markdown h3 + p, && h3 + p':
    {
      marginTop: '0.24em !important',
    },
  '&.wmde-markdown strong, && strong, &.wmde-markdown b, && b': {
    fontWeight: '500 !important',
  },
  '&.wmde-markdown h1 strong, && h1 strong, &.wmde-markdown h2 strong, && h2 strong, &.wmde-markdown h3 strong, && h3 strong, &.wmde-markdown li strong, && li strong, &.wmde-markdown p strong, && p strong':
    {
      fontWeight: '500 !important',
    },
  '&.wmde-markdown hr, && hr': {
    height: '1px !important',
    margin: '0.4em 0 !important',
    opacity: 0.3,
    border: 'none !important',
    padding: '0 !important',
  },
});
